/**
 * Mock config templates — variety across vendors + categories so the
 * filter chips have meaningful groups to render.
 */
import type { ConfigTemplate } from "@/api/schemas/config-templates";

export const MOCK_CONFIG_TEMPLATES: ConfigTemplate[] = [
  {
    slug: "meraki-vlan-block",
    name: "Meraki · VLAN block",
    vendor: "meraki",
    category: "VLAN",
    description: "Renders a single VLAN definition for a Meraki MX appliance — id, subnet, helper.",
    blessed: true,
    updatedAt: "2026-04-22T11:30:00Z",
    body:
`vlans:
  - id: {{vlanId}}
    name: {{vlanName}}
    subnet: {{subnet}}
    appliance_ip: {{applianceIp}}
    dhcp_handling: {{dhcpHandling}}`,
    fields: [
      { name: "vlanId",        label: "VLAN id",          type: "number", defaultValue: 51,            required: true },
      { name: "vlanName",      label: "VLAN name",        type: "text",   defaultValue: "guest-wifi",  required: true },
      { name: "subnet",        label: "Subnet (CIDR)",    type: "text",   defaultValue: "10.21.51.0/24", required: true },
      { name: "applianceIp",   label: "Appliance IP",     type: "text",   defaultValue: "10.21.51.1",  required: true },
      { name: "dhcpHandling",  label: "DHCP handling",    type: "select", defaultValue: "Run a DHCP server", options: [
        { value: "Run a DHCP server", label: "Run a DHCP server" },
        { value: "Relay DHCP",         label: "Relay DHCP" },
        { value: "Do not respond",    label: "Do not respond" },
      ]},
    ],
  },
  {
    slug: "meraki-ssid-corporate",
    name: "Meraki · Corporate SSID (802.1X)",
    vendor: "meraki",
    category: "Wireless",
    description: "Adds a corporate-grade SSID with 802.1X auth bound to the GuestTek Radius server.",
    blessed: true,
    updatedAt: "2026-04-15T09:14:00Z",
    body:
`ssids:
  - number: {{ssidNumber}}
    name: {{ssidName}}
    enabled: true
    auth_mode: 8021x-radius
    radius_servers:
      - host: {{radiusHost}}
        port: 1812
        secret: {{radiusSecret}}
    ip_assignment_mode: Bridge mode
    band_selection: {{bandSelection}}`,
    fields: [
      { name: "ssidNumber",    label: "SSID number (0-14)", type: "number", defaultValue: 1,              required: true },
      { name: "ssidName",      label: "SSID name",           type: "text",   defaultValue: "GuestTek-Corp", required: true },
      { name: "radiusHost",    label: "Radius host/IP",      type: "text",   defaultValue: "172.20.50.111", required: true },
      { name: "radiusSecret",  label: "Radius shared secret",type: "text",   required: true,                hint: "Stored encrypted" },
      { name: "bandSelection", label: "Band selection",      type: "select", defaultValue: "Dual band operation",
        options: [
          { value: "Dual band operation", label: "Dual band operation (2.4 + 5)" },
          { value: "5 GHz band only",     label: "5 GHz only" },
          { value: "2.4 GHz band only",   label: "2.4 GHz only" },
        ]},
    ],
  },
  {
    slug: "aruba-port-template",
    name: "Aruba · Switch port baseline",
    vendor: "aruba",
    category: "Switch",
    description: "Stamps a known-good port profile onto a CX 6300 access port — VLAN, BPDU guard, storm control.",
    blessed: true,
    updatedAt: "2026-04-19T16:00:00Z",
    body:
`interface {{interfaceId}}
  description {{description}}
  no shutdown
  no routing
  vlan access {{accessVlan}}
  spanning-tree bpdu-guard
  storm-control broadcast level 1.00
  {{#poe}}power-over-ethernet{{/poe}}`,
    fields: [
      { name: "interfaceId",  label: "Interface id",        type: "text",   defaultValue: "1/1/1",  required: true },
      { name: "description",  label: "Description",         type: "text",   defaultValue: "guest port", required: true },
      { name: "accessVlan",   label: "Access VLAN",         type: "number", defaultValue: 51,        required: true },
      { name: "poe",          label: "Enable PoE",          type: "boolean", defaultValue: true },
    ],
  },
  {
    slug: "aruba-rf-profile",
    name: "Aruba · RF profile (Wi-Fi 6E hotel)",
    vendor: "aruba",
    category: "Wireless",
    description: "Standard hotel RF profile for AP-655 / AP-635 — channel widths, min RSSI, band steering.",
    blessed: false,
    updatedAt: "2026-03-18T10:00:00Z",
    body:
`rf_profile {{profileName}}
  band {{band}}
  channel_width_24 20
  channel_width_5 80
  min_tx_eirp_dbm {{minTxEirp}}
  band_steering enable`,
    fields: [
      { name: "profileName",  label: "Profile name",        type: "text",   defaultValue: "hotel-floor-mr",  required: true },
      { name: "band",         label: "Band",                type: "select", defaultValue: "all", options: [
        { value: "all", label: "All bands" }, { value: "5", label: "5 GHz" }, { value: "2.4", label: "2.4 GHz" }] },
      { name: "minTxEirp",    label: "Min TX EIRP (dBm)",   type: "number", defaultValue: 12 },
    ],
  },
  {
    slug: "system-snmp-baseline",
    name: "System · SNMP baseline",
    vendor: "system",
    category: "Monitoring",
    description: "Cross-vendor SNMP v3 user + community baseline that Synapse polls.",
    blessed: true,
    updatedAt: "2026-04-01T07:30:00Z",
    body:
`snmp:
  v3:
    username: {{snmpUser}}
    auth_protocol: SHA
    auth_password: {{authPassword}}
    privacy_protocol: AES
    privacy_password: {{privacyPassword}}
    notify_target: {{notifyTarget}}`,
    fields: [
      { name: "snmpUser",         label: "SNMPv3 username",   type: "text", defaultValue: "synapse-poller", required: true },
      { name: "authPassword",     label: "Auth password",      type: "text", required: true, hint: "Stored encrypted" },
      { name: "privacyPassword",  label: "Privacy password",   type: "text", required: true, hint: "Stored encrypted" },
      { name: "notifyTarget",     label: "Notify target host", type: "text", defaultValue: "172.20.30.53",   required: true },
    ],
  },
];
